import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import ExcelImportDemo from '../components/landing/ExcelImportDemo';
import AssistantDemo from '../components/landing/AssistantDemo';
import HeroTablet from '../components/landing/HeroTablet';
import MapaVistas from '../components/landing/MapaVistas';
import { Eyebrow, INSTAGRAM_URL, InstagramIcon, Px, Surcos } from '../components/landing/brand';
import OpinionesSection from '../components/landing/OpinionesSection';
import {
  BloqueComparativa,
  BloqueGratis,
  BloqueSenasa,
  BloqueSoftwareGanadero,
  BloqueTiposDeCampo,
} from '../components/landing/BloquesHome';
import faqHome from '../content/faq/home.json';
import useContacto from '../components/landing/useContacto';
import { prefersReducedMotion } from '../components/landing/useInView';
import { ctaPublico, yaIngresoEnEsteDispositivo } from '../helpers/ctaPublico';
import '../components/landing/landing.css';
import {
  ArrowRight, BarChart3, Bell, Check, ClipboardList, FileCheck2, HeartPulse,
  MessageSquare, Plus, Scale, Tag, TrendingUp, Wallet, WifiOff,
} from 'lucide-react';

// Landing pública con el manual de marca MiCampoDigital v1.0 (Onest + Inter,
// Verde Campo #193D2A, logos oficiales, voseo). Estilos en landing.css (prefijo
// lp-), aislados de la paleta "campo ledger" del resto de la app.
// Secciones: inicio · pilares · cómo funciona · Excel · asistente · mapa ·
// funciones · videos · calculadoras gratis · preguntas · cierre.

// Funciones destacadas (linkean a /funcionalidades#<anchor>). Íconos Lucide (trazo 2 px, extremos redondeados).
const FUNCIONES = [
  { title: 'Caravana electrónica', desc: 'Lectura EID/RFID y peso desde la balanza. Menos tipeo, menos errores.', link: '/funcionalidades#caravana-electronica', Icon: Tag },
  { title: 'Historia de cada animal', desc: 'Altas, pesadas, movimientos y bajas, por caravana.', link: '/funcionalidades#gestion-animales', Icon: ClipboardList },
  { title: 'Preñez y sanidad', desc: 'Tacto por caravana, tasa de preñez y vacías por lote.', link: '/funcionalidades#preñez-salud', Icon: HeartPulse },
  { title: 'Gastos y resultados', desc: 'Gastos, ingresos, stock y libro diario. La rentabilidad real.', link: '/funcionalidades#administracion', Icon: Wallet },
  { title: 'Reportes', desc: 'Indicadores por categoría y ubicación para decidir con datos.', link: '/funcionalidades#reportes', Icon: BarChart3 },
  { title: 'Valuación del stock', desc: 'Tus categorías y pesos, con precios de referencia del mercado.', link: '/funcionalidades#precios-valuacion', Icon: TrendingUp },
  { title: 'Recordatorios', desc: 'Vacunas, vencimientos y tareas, con fecha y prioridad.', link: '/funcionalidades#alertas-notificaciones', Icon: Bell },
  { title: 'Asistente', desc: 'Preguntale a tu rodeo con tus palabras, por texto o por audio.', link: '/funcionalidades#asistente-ia', Icon: MessageSquare },
];

const PILARES = [
  { title: 'Simple de verdad', desc: 'Se usa con las botas puestas y una mano ocupada.' },
  { title: 'Tus datos son tuyos', desc: 'Los exportás cuando quieras, sin intermediarios.' },
  { title: 'Hablamos de campo', desc: 'Rodeo, tacto, parición y caravana. Nada de jerga.' },
  { title: 'Funciona con poca señal', desc: 'En el celular, en el corral, sin capacitación.' },
];

const PASOS = [
  { title: 'Cargás', desc: 'Pesadas, tactos, vacunas y gastos, desde el celular o la compu.', items: ['Con o sin señal', 'Lector de caravana y balanza'] },
  { title: 'Se ordena solo', desc: 'Cada animal guarda su historia por caravana: pesos, preñez, sanidad y movimientos.', items: ['Se sincroniza al volver la señal', 'Todo el equipo ve lo mismo'] },
  { title: 'Decidís', desc: 'Ganancia diaria, vacías por lote, costos y resultado del campo, sin armar planillas.', items: ['Reportes por categoría y potrero', 'Recordatorios de vacunas y tareas'] },
];

const REELS = [
  { src: '/landing/reel1.mp4', poster: '/landing/reel1.jpg', title: 'La caravana, leída en la manga' },
  { src: '/landing/reel2.mp4', poster: '/landing/reel2.jpg', title: 'Basta de pasar el cuaderno a Excel' },
  { src: '/landing/reel3.mp4', poster: '/landing/reel3.jpg', title: 'Todo el campo en la palma de la mano' },
];

const GRATIS = [
  { to: '/precios', num: '$/kg', title: 'Precio de la hacienda', desc: 'Referencia del Mercado de Cañuelas por categoría, en pesos o en dólares.', cta: 'Ver precios' },
  { to: '/calculadora-engorde', num: '+kg', title: 'Calculadora de engorde', desc: 'Margen, costo por kilo ganado y ganancia diaria, con precios de Cañuelas.', cta: 'Calcular' },
  { to: '/calculadora-racion', num: 'MS', title: 'Calculadora de ración', desc: 'Consumo, proteína y energía de la dieta según el peso de tus animales.', cta: 'Calcular' },
  { to: '/calculadora-inventario', num: 'Σ', title: 'Valor del inventario', desc: 'Cuánto vale tu rodeo en pesos y en dólares, categoría por categoría.', cta: 'Calcular' },
];

// Preguntas frecuentes: src/content/faq/home.json (el prerender arma el FAQPage
// del schema desde el mismo archivo, así coincide con lo visible).
const PREGUNTAS = faqHome.preguntas;

const Flecha = () => <ArrowRight aria-hidden="true" />;

// KAN-295: acceso a la sesión a un paso del CTA de registro. En mobile el
// "Ingresar" de la barra pública se esconde bajo 1024 px y quedaba sólo
// dentro del menú ☰ — quien ya tiene cuenta no encontraba cómo entrar.
const LoginHint = () => (
  <p className="lp-login">
    ¿Ya tenés cuenta? <Link to="/login">Ingresá</Link>
  </p>
);

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const handleContacto = useContacto();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [reducedMotion] = useState(prefersReducedMotion);

  // KAN-173: si ya hay sesión iniciada, ENTRAR al sitio por la raíz manda
  // directo a Mi Ganado (replace: el botón "atrás" no rebota). El redirect
  // sólo aplica a la entrada: react-router guarda `idx` en history.state —
  // null/0 en el primer route del tab, >0 al navegar desde adentro. Así el
  // logo del header (Link a "/") desde la app sigue mostrando la landing.
  useEffect(() => {
    if (!user) return;
    const idx = (window.history.state as { idx?: number | null } | null)?.idx;
    const esEntradaAlSitio = idx == null || idx === 0;
    if (esEntradaAlSitio) {
      navigate('/animals', { replace: true });
    }
  }, [user, navigate]);

  // Aparición de bloques al bajar + videos de Instagram que se reproducen solo
  // mientras se ven (no gastan datos si nadie llega hasta ahí).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const bloques = root.querySelectorAll('.lp-rv');
    if (typeof IntersectionObserver === 'undefined' || reducedMotion) {
      bloques.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const rvObs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            rvObs.unobserve(e.target);
          }
        }),
      { threshold: 0.15 },
    );
    bloques.forEach((el) => rvObs.observe(el));

    const vidObs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) v.play().catch(() => undefined);
          else v.pause();
        }),
      { threshold: 0.4 },
    );
    root.querySelectorAll('.lp-reel video').forEach((v) => vidObs.observe(v));

    return () => {
      rvObs.disconnect();
      vidObs.disconnect();
    };
  }, [reducedMotion]);

  useSeoMeta(seoPaginas.home);

  // KAN-289: el CTA sabe si este dispositivo ya inició sesión (→ Ingresar).
  const cta = ctaPublico(user);
  const ctaTo = cta.to;
  const ctaLabel = cta.label;
  const ctaProba = ctaPublico(user, 'Creá tu cuenta y probalo');
  // El hint de login sólo tiene valor cuando el CTA es de registro.
  const mostrarLoginHint = !user && !yaIngresoEnEsteDispositivo();

  return (
    <div className="lp" ref={rootRef}>
      <PublicNav />

      {/* ───────────── 1 · INICIO ───────────── */}
      <section id="inicio" className="lp-hero">
        <div className="lp-wrap">
          <div className="lp-split">
            <div>
              <Eyebrow>Gestión ganadera, simple.</Eyebrow>
              <h1>
                Tu campo,
                <br />
                en números.
              </h1>
              <div className="lp-bar" />
              <p className="lp-lead">
                Cargá pesadas, tactos y gastos desde el corral, <strong>aunque no tengas señal</strong>.
                MiCampoDigital ordena todo y te muestra cómo viene tu rodeo.
              </p>
              <div className="lp-ctas">
                <Link className="lp-btn lp-btn--primary" to={ctaTo}>
                  {ctaLabel} <Flecha />
                </Link>
                <a className="lp-btn lp-btn--outline" href="#como">
                  Mirá cómo funciona
                </a>
              </div>
              {mostrarLoginHint && <LoginHint />}
              <div className="lp-facts">
                <div>
                  <WifiOff className="lp-ico" aria-hidden="true" />
                  <p><b>Sin señal, igual cargás</b><span>Se sincroniza sola cuando vuelve.</span></p>
                </div>
                <div>
                  <Scale className="lp-ico" aria-hidden="true" />
                  <p><b>Balanza y caravana electrónica</b><span>El peso y la caravana entran solos.</span></p>
                </div>
                <div>
                  <FileCheck2 className="lp-ico" aria-hidden="true" />
                  <p><b>Tu Excel, como está</b><span>Lo subís y el asistente lo ordena.</span></p>
                </div>
              </div>
            </div>
            <HeroTablet />
          </div>
          <a className="lp-cue" href="#pilares">
            <i aria-hidden="true" />
            Bajá para ver cómo funciona
          </a>
        </div>
        <Surcos style={{ right: -120, bottom: -260, width: 760 }} />
      </section>

      {/* ───────────── 2 · PILARES ───────────── */}
      <section id="pilares" className="lp-pilares lp-dark" aria-label="Por qué MiCampoDigital">
        <Surcos color="blanco" arcs={3} opacity={0.07} style={{ left: -160, bottom: -300, width: 700 }} />
        <div className="lp-wrap">
          {PILARES.map((p, i) => (
            <div key={p.title} className={`lp-rv lp-d${i}`}>
              <Px />
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ───────────── 2b · QUÉ ES UN SOFTWARE GANADERO ───────────── */}
      <BloqueSoftwareGanadero />

      {/* ───────────── 3 · CÓMO FUNCIONA ───────────── */}
      <section id="como" className="lp-como lp-pad">
        <div className="lp-wrap">
          <div className="lp-sec-h lp-rv">
            <Eyebrow>Cómo funciona</Eyebrow>
            <h2 className="lp-h2">Del corral al número, en tres pasos.</h2>
            <p className="lp-lead">Lo que anotás en la manga hoy, mañana ya es un dato que te sirve para decidir.</p>
          </div>
          <div className="lp-steps">
            {PASOS.map((s, i) => (
              <div key={s.title} className={`lp-step lp-rv lp-d${i} ${i === 0 ? 'lp-step--on' : ''}`}>
                <span className="lp-step__n">0{i + 1}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul>
                  {s.items.map((it) => (
                    <li key={it}>
                      <Check className="lp-ico" aria-hidden="true" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <BloqueTiposDeCampo />

          <div className="lp-phone-row">
            <div className="lp-phone lp-rv">
              <img src="/landing/carga-mobile.jpg" alt="Pantalla Cargar evento en el celular: caravana, categoría, peso y fecha" loading="lazy" />
            </div>
            <div className="lp-rv lp-d1">
              <Eyebrow>En la manga</Eyebrow>
              <h3>Pasás la caravana, la balanza pone el peso.</h3>
              <p>
                Conectás el lector y la balanza. La app te muestra el peso anterior y la fecha de ingreso de cada
                animal, así te das cuenta en el momento si algo no cierra.
              </p>
              <div className="lp-chips">
                <span className="lp-chip"><i className="lp-dot-wait" />Sin señal: queda guardado en el celular</span>
                <span className="lp-chip"><i className="lp-dot-ok" />Vuelve la señal: se sincroniza sola</span>
              </div>
              <BloqueSenasa />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── 4 · EXCEL ───────────── */}
      <section id="excel" className="lp-excel lp-pad">
        <div className="lp-wrap">
          <div className="lp-split">
            <div className="lp-rv">
              <span className="lp-new"><i aria-hidden="true" />Nuevo: importación de Excel</span>
              <h2 className="lp-h2">
                Traé tu Excel como está.
                <br />
                <span>Tu rodeo queda cargado.</span>
              </h2>
              <div className="lp-bar" />
              <p className="lp-lead">
                No hace falta ordenar la planilla. Si las columnas dicen <code>Nº Carav.</code> o <code>Kgs</code>, el
                asistente te propone qué dato es cada una. Revisás, corregís lo que haga falta e importás.
              </p>
              <div className="lp-ctas">
                <Link className="lp-btn lp-btn--primary" to={ctaTo}>
                  {user ? 'Ir a mi panel' : 'Subí tu planilla gratis'} <Flecha />
                </Link>
              </div>
              <div className="lp-also">
                <span><Check className="lp-ico" aria-hidden="true" />Excel o CSV</span>
                <span><Check className="lp-ico" aria-hidden="true" />Varias hojas en un archivo</span>
                <span><Check className="lp-ico" aria-hidden="true" />Corregís antes de importar</span>
              </div>
            </div>
            <div className="lp-rv lp-d1">
              <ExcelImportDemo />
            </div>
          </div>
        </div>
        <Surcos arcs={3} style={{ left: -200, bottom: -280, width: 760 }} />
      </section>

      {/* ───────────── 4b · CUADERNO, EXCEL O SOFTWARE ───────────── */}
      <BloqueComparativa />

      {/* ───────────── 5 · ASISTENTE ───────────── */}
      <section id="asistente" className="lp-asistente lp-pad lp-dark">
        <div className="lp-wrap">
          <div className="lp-split">
            <div className="lp-rv">
              <Eyebrow>Asistente · ya disponible</Eyebrow>
              <h2 className="lp-h2 lp-h2--xl">
                Preguntale
                <br />
                a tu campo.
              </h2>
              <div className="lp-bar" />
              <p className="lp-lead">
                El asistente conoce tu rodeo, tus potreros y tus números. Escribile o mandale un audio desde la manga.
                Si no hay señal, lo recibe apenas vuelve y te contesta.
              </p>
              <div className="lp-ctas">
                <Link className="lp-btn lp-btn--primary" to={ctaProba.to}>
                  {ctaProba.label} <Flecha />
                </Link>
              </div>
              <div className="lp-asks">
                <small>Preguntas de todos los días</small>
                <div>
                  <span>¿Qué tengo que vacunar esta semana?</span>
                  <span>¿Cuántas vacas hay en el Norte?</span>
                  <span>¿Cuánto pesaron los terneros?</span>
                </div>
              </div>
            </div>
            <div className="lp-rv lp-d1">
              <AssistantDemo />
            </div>
          </div>
        </div>
        <Surcos color="blanco" opacity={0.07} style={{ right: -140, bottom: -250, width: 820 }} />
      </section>

      {/* ───────────── 6 · MAPA ───────────── */}
      <section id="mapa" className="lp-mapa lp-pad">
        <div className="lp-wrap">
          <div className="lp-sec-h lp-rv">
            <Eyebrow>Mapa del campo</Eyebrow>
            <h2 className="lp-h2">Cada cuadro, con su pasto y su carga.</h2>
            <p className="lp-lead">
              Dibujás potreros, aguadas y mangas sobre la imagen satelital. La app mide el verde de cada cuadro y te
              dice cuántas raciones aguanta.
            </p>
          </div>
          <MapaVistas />
        </div>
      </section>

      {/* ───────────── 7 · FUNCIONES ───────────── */}
      <section id="funciones" className="lp-funciones lp-pad">
        <div className="lp-wrap">
          <div className="lp-sec-h lp-rv">
            <Eyebrow>Funciones</Eyebrow>
            <h2 className="lp-h2">Todo el establecimiento, en un solo lugar.</h2>
          </div>
          <div className="lp-tools lp-rv">
            {FUNCIONES.map(({ title, desc, link, Icon }) => (
              <Link key={title} className="lp-tool" to={link}>
                <Icon className="lp-ico" aria-hidden="true" />
                <h3>{title}</h3>
                <p>{desc}</p>
                <small>Ver más →</small>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── 8 · VIDEOS ───────────── */}
      <section id="videos" className="lp-reels-sec lp-pad">
        <div className="lp-wrap">
          <div className="lp-reels-h">
            <div className="lp-sec-h lp-rv">
              <Eyebrow>En el campo</Eyebrow>
              <h2 className="lp-h2">Mirala funcionar en 20 segundos.</h2>
            </div>
            <a
              className="lp-btn lp-btn--outline lp-rv"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <InstagramIcon />
              Seguinos en @micampodigital
            </a>
          </div>
          <div className="lp-reels">
            {REELS.map((r, i) => (
              <div key={r.src} className={`lp-reel lp-rv lp-d${i}`}>
                <video src={r.src} poster={r.poster} muted loop playsInline controls={reducedMotion} preload="none" aria-label={r.title} />
                <p>
                  {r.title}
                  <small>Reel · Instagram</small>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── 9 · CALCULADORAS GRATIS ───────────── */}
      <section id="gratis" className="lp-gratis lp-pad">
        <div className="lp-wrap">
          <div className="lp-sec-h lp-rv">
            <Eyebrow>Sin crear cuenta</Eyebrow>
            <h2 className="lp-h2">Herramientas gratis para hacer números hoy.</h2>
            <p className="lp-lead">Usalas sin registrarte. Si después querés que se calculen solas con tus animales, creás la cuenta.</p>
          </div>
          <BloqueGratis />
          <div className="lp-free">
            {GRATIS.map((g, i) => (
              <Link key={g.to} to={g.to} className={`lp-rv lp-d${i}`}>
                <span className="num">{g.num}</span>
                <h3>{g.title}</h3>
                <p>{g.desc}</p>
                <small>{g.cta} →</small>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── OPINIONES (solo si hay aprobadas) ───────────── */}
      <OpinionesSection />

      {/* ───────────── 10 · PREGUNTAS ───────────── */}
      <section id="preguntas" className="lp-faq lp-pad">
        <div className="lp-wrap">
          <div className="lp-split">
            <div className="lp-sec-h lp-rv">
              <Eyebrow>Preguntas</Eyebrow>
              <h2 className="lp-h2">Lo que nos preguntan en la tranquera.</h2>
              <button type="button" className="lp-link" onClick={handleContacto} style={{ marginTop: 26 }}>
                Escribinos por el chat <Flecha />
              </button>
            </div>
            <div className="lp-rv lp-d1">
              {PREGUNTAS.map((p, i) => (
                <details key={p.q} open={i === 0}>
                  <summary>
                    {p.q}
                    <Plus aria-hidden="true" />
                  </summary>
                  <p>{p.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── 11 · CIERRE ───────────── */}
      <section id="empezar" className="lp-cierre lp-pad lp-dark">
        <div className="lp-wrap">
          <div className="lp-rv" style={{ maxWidth: 760 }}>
            <Eyebrow>Gestión ganadera, simple.</Eyebrow>
            <h2 className="lp-h2 lp-h2--xl">
              Dejá la libreta.
              <br />
              Empezá hoy.
            </h2>
            <div className="lp-bar" />
            <p className="lp-lead">Creás la cuenta en un minuto, subís tu Excel y esta misma tarde ya ves tu rodeo en números.</p>
            <div className="lp-ctas">
              <Link className="lp-btn lp-btn--primary" to={ctaTo}>
                {ctaLabel} <Flecha />
              </Link>
              <a className="lp-btn lp-btn--outline" href="#gratis">
                Probá una calculadora
              </a>
            </div>
            {mostrarLoginHint && <LoginHint />}
          </div>
        </div>
        <Surcos color="blanco" style={{ right: -100, bottom: -240, width: 900 }} />
      </section>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
